import React, { useEffect, useState } from "react";
import api from "../api";
import { Lock, Eye, EyeOff, ShieldCheck } from "lucide-react";

/* AdminUnlock — server-side extra lock on /admin/* routes.
   Requires ADMIN_ACCESS_TOKEN (backend .env) in addition to admin JWT.
   Stored in sessionStorage so it clears on tab close. */
export default function AdminUnlock({ children }) {
  const [unlocked, setUnlocked] = useState(false);
  const [checking, setChecking] = useState(true);
  const [token, setToken] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Verify existing sessionStorage token, if any, on mount.
  useEffect(() => {
    const stored = sessionStorage.getItem("tgc_admin_access");
    if (!stored) { setChecking(false); return; }
    api.post("/admin/unlock", { token: stored })
      .then((r) => { if (r.data?.success) setUnlocked(true); else sessionStorage.removeItem("tgc_admin_access"); })
      .catch(() => sessionStorage.removeItem("tgc_admin_access"))
      .finally(() => setChecking(false));
  }, []);

  const submit = async (e) => {
    e && e.preventDefault();
    if (!token.trim()) { setErr("Введите admin access token"); return; }
    setBusy(true); setErr("");
    try {
      // First store optimistically — the interceptor will attach it to /admin/unlock too.
      sessionStorage.setItem("tgc_admin_access", token.trim());
      const r = await api.post("/admin/unlock", { token: token.trim() });
      if (r.data?.success) {
        setUnlocked(true);
      } else {
        sessionStorage.removeItem("tgc_admin_access");
        setErr("Не подтверждено сервером");
      }
    } catch (e2) {
      sessionStorage.removeItem("tgc_admin_access");
      setErr(e2.response?.status === 401 ? "Неверный token" : (e2.response?.data?.detail || "Ошибка"));
    } finally { setBusy(false); }
  };

  const relock = () => {
    sessionStorage.removeItem("tgc_admin_access");
    setUnlocked(false); setToken("");
  };

  if (checking) return <div style={{ padding: 40, color: "var(--tg-text-hint)" }}>Проверка admin-токена…</div>;

  if (unlocked) {
    return (
      <>
        <div style={{ padding: "8px 14px", background: "rgba(16,244,167,0.06)", border: "1px solid rgba(16,244,167,0.25)", borderRadius: 8, marginBottom: 14, fontSize: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ color: "#10F4A7" }}><ShieldCheck size={12} style={{ verticalAlign: "middle", marginRight: 6 }} />Admin-доступ разблокирован (сессия)</span>
          <button onClick={relock} data-testid="admin-relock"
            style={{ background: "transparent", border: "1px solid #FF3D6C", color: "#FF3D6C", padding: "3px 10px", borderRadius: 6, fontSize: 11, cursor: "pointer" }}>
            🔒 Заблокировать
          </button>
        </div>
        {children}
      </>
    );
  }

  return (
    <div data-testid="admin-unlock-gate"
      style={{ maxWidth: 480, margin: "60px auto", padding: 32, background: "#0B1220", border: "1px solid #FFD700", borderRadius: 16, boxShadow: "0 20px 60px rgba(255,215,0,0.12)" }}>
      <div style={{ textAlign: "center", marginBottom: 22 }}>
        <div style={{ display: "inline-flex", padding: 16, background: "rgba(255,215,0,0.08)", borderRadius: "50%", marginBottom: 12 }}>
          <Lock size={28} color="#FFD700" />
        </div>
        <h2 style={{ margin: 0, fontSize: 22, color: "#FFD700" }}>Admin Access Lock</h2>
        <p style={{ color: "var(--tg-text-hint)", fontSize: 13, marginTop: 8 }}>
          Раздел администратора защищён серверным токеном.<br />
          Введите <b>ADMIN_ACCESS_TOKEN</b>, установленный в <code>.env</code>.
        </p>
      </div>
      <form onSubmit={submit} style={{ display: "grid", gap: 10 }}>
        <div style={{ display: "flex", gap: 6 }}>
          <input type={show ? "text" : "password"} value={token} onChange={(e) => setToken(e.target.value)}
            placeholder="Admin access token"
            autoFocus data-testid="admin-unlock-input"
            className="auth-input"
            style={{ flex: 1, paddingLeft: 14 }} />
          <button type="button" onClick={() => setShow(!show)} data-testid="admin-unlock-toggle"
            style={{ width: 44, borderRadius: 8, border: "1px solid var(--tg-border)", background: "rgba(0,0,0,0.3)", color: "var(--tg-text)", cursor: "pointer" }}>
            {show ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        {err && <div style={{ color: "#FF3D6C", fontSize: 12 }} data-testid="admin-unlock-err">⚠ {err}</div>}
        <button type="submit" disabled={busy} className="tg-btn primary" data-testid="admin-unlock-submit"
          style={{ justifyContent: "center", opacity: busy ? 0.6 : 1 }}>
          {busy ? "Проверка…" : "🔓 Разблокировать"}
        </button>
      </form>
      <div style={{ marginTop: 20, padding: 12, background: "rgba(255,61,108,0.05)", border: "1px solid rgba(255,61,108,0.2)", borderRadius: 8, fontSize: 11, color: "var(--tg-text-hint)" }}>
        Даже при утечке пароля админа злоумышленник не сможет войти без этого токена.
        Токен хранится только в session (закрытие вкладки — сбрасывается).
      </div>
    </div>
  );
}
